import { Component, type ErrorInfo, type ReactNode } from "react";

interface State {
  hasError: boolean;
  error?: Error;
  errorId?: string;
}

/** Cheap human-readable error id. Not cryptographically secure — just
 *  needs to be unique enough that a recipient can quote it back to
 *  their rep and we can find the matching console log entry. */
function newErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `ERR-${ts}-${rand}`.toUpperCase();
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorId: newErrorId() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `Uncaught render error [${this.state.errorId}]:`,
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback error={this.state.error} errorId={this.state.errorId} />
      );
    }
    return this.props.children;
  }
}

export function ErrorFallback({
  error,
  errorId,
}: {
  error?: Error;
  errorId?: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg p-6">
      <div className="auth-shell">
        <div className="card p-8 text-center">
          <h1 className="mb-2 text-2xl font-semibold text-brand-text">
            Something went wrong
          </h1>
          <p className="text-brand-muted">
            {error?.message ?? "Please refresh the page and try again."}
          </p>
          {errorId && (
            <p className="mt-3 text-xs text-brand-muted">
              If you can't recover, please contact your Payment Products
              representative with this code:{" "}
              <code className="rounded bg-brand-bg px-1.5 py-0.5 text-brand-text">
                {errorId}
              </code>
            </p>
          )}
          <button
            className="btn-primary mt-6 py-2"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
